import React, { useState, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import '../pages/billProfile.css';
import API from "../utils/API";
import { AuthContext } from "../utils/AuthContext";

export default function BillContacts({ billId, billAuthorOrgId }) {
    const { auth, isAdmin } = useContext(AuthContext);
    const navigate = useNavigate();
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);
    const [editContacts, setEditContacts] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [userTeams, setUserTeams] = useState([]); // [{ team_id, team_name }]
    const [addScopeTeamId, setAddScopeTeamId] = useState(""); // "" = personal
    const [shareDropdownContactId, setShareDropdownContactId] = useState(null);
    const dropdownRef = useRef(null);

    useEffect(() => {
        if (!auth.user) return;
        API.getUserTeams({ user_id: auth.user.user_id })
            .then(res => setUserTeams((res.data || []).map(m => ({
                team_id: m.team_id,
                team_name: m.TeamAccount?.team_name || 'Workspace',
                team_acronym: m.TeamAccount?.team_acronym || m.TeamAccount?.team_name || 'Workspace',
            }))))
            .catch(err => console.error("Error fetching user teams:", err));
    }, [auth.user]);

    const userTeamIds = new Set(userTeams.map(t => t.team_id));
    const teamNameById = Object.fromEntries(userTeams.map(t => [t.team_id, t.team_name]));

    const fetchContacts = async () => {
        if (!billId || !auth.user) return;
        setLoading(true);
        try {
            const response = await API.getBillContacts(billId);

            const sorted = response.data
                .filter(contact => contact.user_id === auth.user.user_id || contact.user_id === null || (contact.team_id && userTeamIds.has(contact.team_id)))
                .sort((a, b) => {
                const jobA = a.ProfileInfo?.JobExperiences?.[0];
                const jobB = b.ProfileInfo?.JobExperiences?.[0];

                const isAuthorStaffA = jobA?.organization_id === billAuthorOrgId;
                const isAuthorStaffB = jobB?.organization_id === billAuthorOrgId;
                if (isAuthorStaffA && !isAuthorStaffB) return -1;
                if (!isAuthorStaffA && isAuthorStaffB) return 1;

                const isLegislativeA = jobA?.Organization?.group_type === 'Legislative';
                const isLegislativeB = jobB?.Organization?.group_type === 'Legislative';
                if (isLegislativeA && !isLegislativeB) return -1;
                if (!isLegislativeA && isLegislativeB) return 1;
                
                if (isLegislativeA && isLegislativeB) {
                    const isCommitteeA = jobA?.Organization?.organization_name.toLowerCase().includes('committee');
                    const isCommitteeB = jobB?.Organization?.organization_name.toLowerCase().includes('committee');
                    if (isCommitteeA && !isCommitteeB) return -1;
                    if (!isCommitteeA && isCommitteeB) return 1;
                }

                return a.ProfileInfo.last_name.localeCompare(b.ProfileInfo.last_name);
            });

            setContacts(sorted);
        } catch (error) {
            console.error("Error fetching bill contacts:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchContacts();
    }, [billId, auth.user, billAuthorOrgId, userTeams]);

    useEffect(() => {
        if (searchTerm.length < 2) {
            setSearchResults([]);
            return;
        }
        const searchProfiles = async () => {
            try {
                const response = await API.searchProfilesForContact(searchTerm.trim());
                const existingContactIds = new Set(contacts.map(c => c.profile_id));
                setSearchResults(response.data.filter(p => !existingContactIds.has(p.profile_id)));
            } catch (error) {
                console.error("Error searching profiles:", error);
            }
        };
        const delayDebounceFn = setTimeout(() => {
            searchProfiles();
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm, contacts]);

    const handleAddContact = async (profileId) => {
        try {
            await API.addBillContact({
                bill_id: billId,
                profile_id: profileId,
                user_id: auth.user.user_id,
                team_id: addScopeTeamId || null,
            });
            setSearchTerm("");
            setSearchResults([]);
            fetchContacts();
        } catch (error) {
            console.error("Error adding contact:", error);
        }
    };

    const handleDeleteContact = async (contactId) => {
        try {
            await API.deleteBillContact(contactId);
            fetchContacts();
        } catch (error) {
            console.error("Error deleting contact:", error);
        }
    };

    const handleMakeUniversal = async (contactId) => {
        try {
            await API.makeContactUniversal(contactId);
            fetchContacts();
        } catch (error) {
            console.error("Error making contact universal:", error);
        }
    };

    const handleDeUniversalize = async (contactId) => {
        try {
            await API.deUniversalizeContact(contactId, auth.user.user_id);
            fetchContacts();
        } catch (error) {
            console.error("Error making contact personal:", error);
        }
    };

    const handleShareWithTeam = async (contactId, teamId) => {
        try {
            await API.shareContactWithTeam(contactId, teamId, auth.user.user_id);
            fetchContacts();
        } catch (error) {
            console.error("Error sharing contact with Workspace:", error);
        }
    };

    const handleMakePersonal = async (contactId) => {
        try {
            await API.makeContactPersonal(contactId, auth.user.user_id);
            fetchContacts();
        } catch (error) {
            console.error("Error making contact personal:", error);
        }
    };
    
    const handleKeyDown = (e) => {
        if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightedIndex(prev => Math.max(prev - 1, 0));
        } else if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlightedIndex(prev => Math.min(prev + 1, searchResults.length - 1));
        } else if (e.key === "Enter" && highlightedIndex !== -1) {
            e.preventDefault();
            handleAddContact(searchResults[highlightedIndex].profile_id);
        }
    };

    const formatPhoneNumber = (phone) => {
        if (!phone) return '';
        const cleaned = ('' + phone).replace(/\D/g, '');
        const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
        if (match) {
            return `(${match[1]}) ${match[2]}-${match[3]}`;
        }
        return phone;
    }

    return (
        <div className="card shadow-sm mb-3">
            <div className="card-header bg-white d-flex ">
                <strong>Contacts</strong>
                <span className="eg-tooltip-wrap ms-1">
                    <i className="bi bi-info-circle eg-tooltip-icon" />
                    <span className="eg-tooltip">Contacts you add are personal to your account. Contacts marked with the Workspace icon <i className="bi bi-people-fill"/> are shared with your Workspace. Contacts marked with the universal access icon <i className="bi bi-universal-access-circle"/> are visible to all users.</span>
                </span>
                <span className="contacts-icon-group">
                    <i className={`bi contact-head-icon me-3 ${!showAdd ? 'bi-plus-circle' : 'bi-dash-circle'}`}
                        title="Add personal contact for bill"
                        onClick={() => setShowAdd(!showAdd)}
                    />
                    <i className={`bi contact-head-icon ${editContacts === false ? 'bi-pencil-square ' : 'bi-check2-square'}`} 
                        title="Edit contact list"
                        alt="Edit Contact Button" 
                        onClick={() => setEditContacts(!editContacts)}                    
                    />
                </span>
            </div>
            <div className="card-body pt-2 pb-0">
                {showAdd && (
                    <div className="mb-3">
                        <div className="input-group input-group-sm">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search for a person to add..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                        {userTeams.length > 0 && (
                            <div className="contact-scope-toggle">
                                <button
                                    type="button"
                                    className={`contact-scope-btn ${addScopeTeamId === "" ? "active" : ""}`}
                                    onClick={() => setAddScopeTeamId("")}
                                >
                                    <i className="bi bi-person-fill me-1" />Personal
                                </button>
                                {userTeams.map(team => (
                                    <button
                                        type="button"
                                        key={team.team_id}
                                        className={`contact-scope-btn ${addScopeTeamId === String(team.team_id) ? "active" : ""}`}
                                        onClick={() => setAddScopeTeamId(String(team.team_id))}
                                        title={team.team_name}
                                    >
                                        <i className="bi bi-people-fill me-1" />{team.team_acronym}
                                    </button>
                                ))}
                            </div>
                        )}
                        {searchResults.length > 0 && (
                            <ul className="list-group mt-1" ref={dropdownRef}>
                                {searchResults.map((profile, index) => (
                                    <li
                                        key={profile.profile_id}
                                        className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center p-2 ${index === highlightedIndex ? "active" : ""}`}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => handleAddContact(profile.profile_id)}
                                    >
                                        <div>
                                            <span className="bill-contact-name">{profile.first_name} {profile.last_name} </span>
                                            {profile.JobExperiences?.[0] && (
                                                <span className="bill-contact-info">, {profile.JobExperiences[0].Division?.division_name || profile.JobExperiences[0].Organization?.organization_name}</span>
                                            )}
                                        </div>
                                        <i className="bi bi-plus-circle" />
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}

                {loading ? (
                    <p>Loading contacts...</p>
                ) : contacts.length > 0 ? (
                    <ul className="list-unstyled" style={contacts.length > 4 ? { maxHeight: '250px', overflowY: 'auto' } : {}}>
                        {contacts.map((contact, index) => {
                            const profile = contact.ProfileInfo;
                            const job = profile?.JobExperiences[0];
                            const orgName = job?.Division?.division_name || job?.Organization?.organization_name || '';
                            return (
                                <li key={contact.bill_contact_id} className={`mb-2 pb-2 me-2 ${index === contacts.length - 1 ? '' : 'border-bottom'}`}>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="d-flex flex-column">
                                            <span className="bill-contact-name" onClick={() => navigate(`/profile/${profile.profile_id}`)}>{profile.first_name} {profile.last_name}                                    
                                            </span>
                                            <span className="text-muted bill-contact-info">{orgName}</span>
                                        </div>
                                        <div className="bill-contact-info d-flex flex-column align-items-end ms-2 ">
                                            <span className="text-muted " style={{whiteSpace: 'nowrap'}}>{formatPhoneNumber(profile.other_phone)}</span>
                                            <div className="d-flex align-items-center gap-3 mt-1">
                                                {!editContacts && contact.user_id === null && (
                                                    <i className="bi bi-universal-access-circle universal-contact-icon" title="All users can view as a universal contact for this bill" />
                                                )}
                                                {!editContacts && contact.user_id !== null && contact.team_id && (
                                                    <i className="bi bi-people-fill universal-contact-icon" title={`Shared with ${teamNameById[contact.team_id] || 'your Workspace'}`} />
                                                )}
                                                <a href={`mailto:${profile.work_email}`} className="text-muted" title={`Send email to ${profile.first_name} ${profile.last_name}`}><i className="bi bi-envelope-fill bill-contact-email"></i></a>
                                                {editContacts && auth.user.contributor && contact.user_id !== null && (
                                                    <i className="bi bi-universal-access-circle universal-access-icon" title="Make contact public" onClick={() => handleMakeUniversal(contact.bill_contact_id)}></i>
                                                )}
                                                {editContacts && isAdmin && contact.user_id === null && (
                                                    <i className="bi bi-person-fill universal-access-icon" title="Make contact personal (Admin)" onClick={() => handleDeUniversalize(contact.bill_contact_id)}></i>
                                                )}
                                                {editContacts && contact.user_id === auth.user.user_id && !contact.team_id && userTeams.length > 0 && (
                                                    <span className="position-relative">
                                                        <i className="bi bi-people-fill universal-access-icon" title="Share with your Workspace" onClick={() => {
                                                            if (userTeams.length === 1) {
                                                                handleShareWithTeam(contact.bill_contact_id, userTeams[0].team_id);
                                                            } else {
                                                                setShareDropdownContactId(shareDropdownContactId === contact.bill_contact_id ? null : contact.bill_contact_id);
                                                            }
                                                        }}></i>
                                                        {shareDropdownContactId === contact.bill_contact_id && (
                                                            <select
                                                                className="form-select form-select-sm bill-contact-share-select"
                                                                defaultValue=""
                                                                onChange={(e) => {
                                                                    if (e.target.value) handleShareWithTeam(contact.bill_contact_id, e.target.value);
                                                                    setShareDropdownContactId(null);
                                                                }}
                                                            >
                                                                <option value="" disabled>Share with…</option>
                                                                {userTeams.map(team => (
                                                                    <option key={team.team_id} value={team.team_id}>{team.team_name}</option>
                                                                ))}
                                                            </select>
                                                        )}
                                                    </span>
                                                )}
                                                {editContacts && contact.user_id === auth.user.user_id && contact.team_id && (
                                                    <i className="bi bi-people-fill universal-access-icon" title="Make personal (unshare from Workspace)" onClick={() => handleMakePersonal(contact.bill_contact_id)}></i>
                                                )}
                                                {editContacts && contact.user_id === auth.user.user_id && (
                                                    <i className="bi bi-trash3 delete-contact-icon" onClick={() => handleDeleteContact(contact.bill_contact_id)}></i>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                ) : (
                    <p className="text-muted bp-placeholder-text"><i>No contacts added for this bill.</i></p>
                )}
            </div>
        </div>
    );
}